import {Injectable} from '@angular/core';
import Modeler from 'bpmn-js/lib/Modeler.js';
import {getModelerExtensionModule} from '@ispm-client/modules/bpmn-modeler/bpmn/modeler-extension-module';
import {getModelerCommandExtensionModule} from '@ispm-client/modules/bpmn-modeler/bpmn/commands/commands-extension-module';
import {ModelerInterceptorsExtensionModule} from '@ispm-client/modules/bpmn-modeler/bpmn/Interceptors/modeler-interceptors-extension-module';
import {Store, select} from '@ngrx/store';
import {State} from '@ispm-client/modules/bpmn-modeler/store/state';
import {customModdle} from '@ispm/shared/bpmn-io/pathways-meta-data-model';
import {Actions, ofType} from '@ngrx/effects';
import {
  BpmnProcessCreatorActionTypes,
  GenerateBpmnXml,
  GenerateBpmnXmlFailure,
  GenerateBpmnXmlSuccess
} from '@ispm-client/modules/bpmn-modeler/store/actions';
import {BPMN_RENDERER, TEXT_RENDERER, TEXT_RENDERER_FOR_IE11} from '@ispm/shared/bpmn-io/bpmn-viewer-styling';
import {PathwaysAuthoringState} from '@ispm/shared/constants/pathways-authoring-state';
import {ModelerTrackingChangesExtensionModule} from '@ispm-client/modules/bpmn-modeler/bpmn/tracking-changes/modeler-tracking-changes-extension-module';
import {EventType, InjectionNames} from '@ispm/shared/bpmn-io/bpmn-js';
import {ModelerReadOnlyExtensionModule} from '@ispm-client/modules/bpmn-modeler/bpmn/modeler-read-only-extension-module';
import {flatten, isEmpty, uniqBy, get} from 'lodash';
import {IEService} from '@ispm/shared/services/IE.service';
import {END_NODE_ID, processType} from '@ispm-shared/modules/bpmn/diagram-element-terms';
import {NodeSelectionDependencies} from "@ispm-client/modules/bpmn-modeler/service/node-selection-deps.service";
import {NodeDependencies} from "@ispm/shared/bpmn/models/node-dependencies";
import {BpmnModelerBehaviorConfig} from "@ispm-client/modules/bpmn-modeler/models/bpmn-modeler-config";
import {BpmnNodeCreatedResult} from "@ispm-client/modules/bpmn-modeler/models/bpmn-node-created-result";
import {TreatmentPlanSelectorResult} from "@ispm-client/modules/bpmn-modeler/models/treatment-plan-selector-result";
import {ModelingUtil} from "@ispm/shared/bpmn-io/modeling-util";
import {NodeCreationResult} from "@ispm-client/modules/bpmn-modeler/models/node-creation-result";
import {getVerticalPosition, getVerticalLayoutVerticalPosition} from "@ispm-client/modules/bpmn-modeler/bpmn/AutoPlacePathways";
import {BpmnRegistryNode} from "@ispm-client/modules/bpmn-modeler/models/bpmn-registry-node";
import {MapUtils} from "@ispm/shared/utils/Map.utils";
import {Shape} from "diagram-js/lib/model";
import {NodeType} from "@ispm/shared/bpmn-io/node-type";
import {Observable} from "rxjs";
import {selectNodeCreationInfoById} from "@ispm-client/modules/bpmn-modeler/store/selectors";
import {first, take} from "rxjs/operators";
import {TreatmentNodeUtils} from "@ispm/shared/bpmn-io/treatment-node-utils";

@Injectable()
export class BpmnModelerService {

    private modeler;
    private performTreeRepair: string = undefined;

    constructor(private actions$: Actions,
                private store$: Store<State>,
                private ieService: IEService) {
        this.actions$
            .pipe(ofType<GenerateBpmnXml>(BpmnProcessCreatorActionTypes.GENERATE_BPMN_XML))
            .subscribe((action) => {
               this.saveXml(action.payload.onSuccess);
            });
    }

    async saveXml(onSuccess) {
      try {
        const result = await this.modeler.saveXML({format: true});
        this.onGenerateXMLCompleted(result.xml, onSuccess);
      }catch (err) {
        console.error(err);
        if (err.message.trim().toLowerCase() === 'no definitions loaded') {
          this.onGenerateXMLCompleted(undefined, onSuccess);
        } else {
          this.store$.dispatch(new GenerateBpmnXmlFailure({error: err}));
        }
      }

    }

    getBpmnModeler(state: PathwaysAuthoringState, currentIdCounter: number, performTreeRepair: string,
                   behavior: BpmnModelerBehaviorConfig, verticalLayoutMode: boolean) {
        if (this.modeler == null) {
            this.initBpmnModeler(state, currentIdCounter, performTreeRepair, behavior, verticalLayoutMode);
        }
        this.performTreeRepair = performTreeRepair;
        return this.modeler;
    }

     onGenerateXMLCompleted( xml, onSuccess) {
        this.store$.dispatch(new GenerateBpmnXmlSuccess({bpmnXml: xml,onSuccess}));
    }

    public getNode(nodeId: string) {
        return this.getElementRegistry().get(nodeId);
    }

    public getNodeName(nodeId: string) {
        const element = this.getNode(nodeId);
        if (element && element.businessObject) {
            return element.businessObject.name;
        }
        return '';
    }

    public updateNode(nodeId: string) {
        const element = this.getNode(nodeId);
        const modeling = this.modeler.get(InjectionNames.modeling);
        const headNodeName = element.businessObject ? element.businessObject.name : element.name;
        modeling.updateProperties(element, {name: headNodeName});
    }

    public prepareForFinalDeployment() {
        this.getPathwaysModeling().processBpmnForApproval();
    }

    public prepareForPreviewBpmn() {
        this.getPathwaysModeling().processBpmnForPreview();
    }

    public restoreBpmnBeforeApproval() {
      this.getPathwaysModeling().restoreBpmnBeforeApproval();
    }

    public prepareForSave() {
        this.getEventBus().fire(EventType.SAVE_START);
    }

    public prepareForPreview() {
        this.getEventBus().fire(EventType.START_PREVIEW);
    }

    /**
     * find ExclusiveGateway element parents by ids
     * @param ids
     */
    findGatewaysRelatedNode(ids: Array<string>) {
        const nodesArray = ids.map(id => this.getNode(id))
        // if its an element in a sub-process node, get the sub-process
            .map(gateway => gateway.parent.type === processType ? gateway.incoming[0].source : gateway.parent);
        return uniqBy(flatten(nodesArray), 'id');
    }

    public navigateToNode(nodeId: string, openPanel: boolean) {
        const pathwaysViewerCommands = this.modeler.get(InjectionNames.pathwaysViewerCommands);
        pathwaysViewerCommands.navigateToNode(nodeId, openPanel);
    }

    public navigateToNodeWithData(nodeId: string, openPanel: boolean, data: any) {
      const pathwaysViewerCommands = this.modeler.get(InjectionNames.pathwaysViewerCommands);
      pathwaysViewerCommands.navigateToNodeWithData(nodeId, openPanel, data);
    }

    public resetModeler() {
        this.modeler = null;
    }

    public computeSelectionDependencyMap(): NodeDependencies {
        const elementRegistry = this.getElementRegistry();
        return NodeSelectionDependencies.computeSelectionDependencyMap(elementRegistry);
    }

    public validateNodesCreationInfo(nodesCreationInfo: Map<string, BpmnNodeCreatedResult<NodeCreationResult | TreatmentPlanSelectorResult>>) {
        if (nodesCreationInfo && Object.keys(nodesCreationInfo).length > 0) {
            const nodeKeys = Object.keys(nodesCreationInfo);

            nodeKeys.forEach((nodeKey) => {
                const bpmnNode = this.getNode(nodeKey);
                if (bpmnNode && nodesCreationInfo[nodeKey] && nodesCreationInfo[nodeKey].id) {
                    this.forceUpdateNodeParentsId(bpmnNode, nodesCreationInfo[nodeKey]);

                } else {
                    delete nodesCreationInfo[nodeKey];
                }
            });
        }
        return nodesCreationInfo;
    }

    public validateFloatingNodes(floatingNodesMap: Map<string,Array<string>>) {
      floatingNodesMap.forEach((nodes,layer) => {
            if (this.getNode(layer) === undefined) {
              floatingNodesMap.delete(layer);
            } else {
              const existedNodes = this.filterExistedNodes(nodes);
              if(isEmpty(existedNodes)){
                floatingNodesMap.delete(layer);
              } else {
                floatingNodesMap.set(layer,existedNodes);
              }
            }
        });
        return MapUtils.convertFromMapToObject(floatingNodesMap);
    }

    public bpmnValidation() {
        this.handleInvalidNodes();
    }

    public clearNodeSelection(){
      this.modeler.get(InjectionNames.selection).select(null);
    }

  public updateTreatmentNodesProperties(nodes: string[], data: any) {
    nodes.forEach(nodeId => {
      let node = this.getNode(nodeId);
      this.updateTreatmentNodeProperties(node, data);
    });
  }

  public updateTreatmentNodeProperties(node: Shape, nodeCreationResult: NodeCreationResult) {
    if (ModelingUtil.getNodeType(node) === NodeType.treatment) {
      this.getNodeCreationInfo(node).pipe(first()).subscribe((nodes: any[]) => {
        if (nodes && nodes.length > 0 && nodes[0]) {
          this.setTreatmentNodeName(node, nodes[0].data?.treatmentPlans, get(nodeCreationResult, "clinicalTrial", undefined));
        }
      });
    }
  }

  private setTreatmentNodeName(node: Shape, treatmentPlans: any[], clinicalTrial: any) {
    const modeling = this.modeler.get(InjectionNames.modeling);
    const treatmentPlanNodeName = TreatmentNodeUtils.createName(treatmentPlans, clinicalTrial);
    modeling.updateProperties(node, {name: treatmentPlanNodeName});
  }

  public getNodeCreationInfo(node: Shape): Observable<object> {
    return this.store$.pipe(select(selectNodeCreationInfoById([node.id])), take(1));
  }

  private handleInvalidNodes() {
    Object.values(this.getElementRegistry().elements).forEach((shape: BpmnRegistryNode) => {
      this.isTreatmentNotConnectedToEndNode(shape.element) || this.isInvalidFloatedNode(shape.element)
    });
  }

    private getElementRegistry() {
        return this.modeler.get(InjectionNames.elementRegistry);
    }

    private getModeling() {
        return this.modeler.get(InjectionNames.modeling);
    }

    private getPathwaysModeling() {
        return this.modeler.get(InjectionNames.pathwaysModeling);
    }

    private getEventBus() {
        return this.modeler.get(InjectionNames.eventBus);
    }

    private isTreatmentNotConnectedToEndNode(node) {
        if (ModelingUtil.isTreatmentNode(node)) {
            const endNode = this.getNode(END_NODE_ID);
            if (!ModelingUtil.hasConnectionToEndNode(node)) {
                if (endNode) {
                    this.getModeling().connect(node, endNode);
                    return true;
                }
            }
        }
        return false;
    }

    private isInvalidFloatedNode(node) {
      if (ModelingUtil.isFloatedGateway(node) || (ModelingUtil.nodeIsFloated(node) && (ModelingUtil.hasDeletionTrackingChange(node) || ModelingUtil.isNeighborhoodHeadNode(node)))) {
        this.getPathwaysModeling().removeNode(node, false, false);
        return true;
      }
      return false;
    }

    private forceUpdateNodeParentsId(node, nodeCreationInfo: BpmnNodeCreatedResult<NodeCreationResult | TreatmentPlanSelectorResult>) {
        if (this.performTreeRepair === 'true' && !ModelingUtil.hasDeletionTrackingChange(node)) {
            nodeCreationInfo.parentNodeIds = ModelingUtil.getNodeParentsIds(node);
        }
    }

    private filterExistedNodes(nodesIDs) {
        return nodesIDs.filter(nodeID => this.getNode(nodeID) !== undefined);
    }

    private initBpmnModeler(state: PathwaysAuthoringState, currentIdCounter: number, performTreeRepair: string,
                            behavior: BpmnModelerBehaviorConfig, verticalLayoutMode: boolean): void {
        const modulesList = this.getModulesList(state);
        const options = {
            container: '#js-canvas',
            width: '100%',
            additionalModules: modulesList,
            moddleExtensions: {
                pw: customModdle
            },
            state: state,
            currentIdCounter: currentIdCounter ? currentIdCounter : 0,
            performTreeRepair,
            behavior,
            verticalLayoutMode,
            keyboard: {bind: document}
        };
        if (this.ieService.isIE()) {
            Object.assign(options, BPMN_RENDERER, TEXT_RENDERER_FOR_IE11);

        } else {
            Object.assign(options, BPMN_RENDERER, TEXT_RENDERER);
        }
        this.modeler = new Modeler(options);
        if (verticalLayoutMode) {
            this.modeler.on('autoPlace', getVerticalLayoutVerticalPosition);
        } else {
            this.modeler.off('autoPlace', getVerticalPosition);
        }
    }

    private getModulesList(state: PathwaysAuthoringState) {
        switch (state) {
            case PathwaysAuthoringState.inReview:
                return [getModelerExtensionModule(this.store$), getModelerCommandExtensionModule(), ModelerInterceptorsExtensionModule,
                    ModelerTrackingChangesExtensionModule];
            case PathwaysAuthoringState.readonly:
                return [getModelerExtensionModule(this.store$), getModelerCommandExtensionModule(), ModelerInterceptorsExtensionModule,
                    ModelerReadOnlyExtensionModule];
            default:
                return [getModelerExtensionModule(this.store$), getModelerCommandExtensionModule(), ModelerInterceptorsExtensionModule];
        }
    }
}
